// Which FAQ the /faq page shows. The web Worker writes the live FAQ (or, when the API is down, its
// latest copy) into the page it serves, so the first render already has it; otherwise the page
// asks GET /api/public/faq (lib/queries.ts faqQuery) and shows a placeholder until it answers.
// The FAQ as first written in code (FAQ_GROUPS) only seeds the database. The browser shows it in
// one case: the pre-rendered page arrived without the Worker's copy (the API was down and this
// colo had never kept one), so it is already on screen; the browser keeps it only until the live
// FAQ answers, and drops it if that fails. The structured data follows what the page shows
// (lib/seo.ts).
import { FAQ_GROUPS, type FaqGroup } from '@gaintplay/core/faq';
import { FAQ_DATA_ID, faqFromJson } from '../pages/info/faq-markup';

/** How the page was opened, read once as the app starts. */
const opened =
  typeof document === 'undefined'
    ? null
    : {
        path: location.pathname,
        prerendered: document.documentElement.hasAttribute('data-prerendered'),
      };

export interface InitialFaq {
  groups: FaqGroup[];
  /** The Worker's copy, live (`at` is when it arrived) or kept from earlier (`at` 0: ask again). */
  source: 'served' | 'built-in';
  at: number;
}

let initial: InitialFaq | null | undefined;

/** What /faq can show before the API answers, or null (a placeholder until it does). */
export function initialFaq(): InitialFaq | null {
  if (initial !== undefined) return initial;
  // The build's pre-render: the FAQ it was built with.
  if (!opened) return (initial = { groups: FAQ_GROUPS, source: 'built-in', at: 0 });
  try {
    const block = document.getElementById(FAQ_DATA_ID)?.textContent;
    const data = block ? (JSON.parse(block) as { groups?: unknown; fresh?: boolean }) : null;
    const groups = data ? faqFromJson(data.groups) : null;
    if (groups)
      return (initial = { groups, source: 'served', at: data?.fresh === false ? 0 : Date.now() });
  } catch {
    /* a broken block: as if there were none */
  }
  if (opened.prerendered && /^\/faq\/?$/.test(opened.path))
    return (initial = { groups: FAQ_GROUPS, source: 'built-in', at: 0 });
  return (initial = null);
}

let shown: readonly FaqGroup[] | null | undefined;

/** The /faq page reports what it shows (null: no questions on screen), for the structured data. */
export function setShownFaq(groups: readonly FaqGroup[] | null) {
  shown = groups;
}

/** What /faq shows now, or null when it shows no questions. */
export const currentFaq = (): readonly FaqGroup[] | null =>
  shown !== undefined ? shown : (initialFaq()?.groups ?? null);
